import type {
  ApiCoverageResponse,
  ApiMaxAgeFirstInvoiceResponse,
  ApiMaxAgeOtherInvoicesResponse,
  ApiMinimumAgeResponse,
  ApiStipulatorResponse,
  ApiUniformCapitalResponse,
} from '../../types/vida/contract/contractType';
import { VIDA_CONTRACTS_ROUTES } from '../requestsService';

function buildQueryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export class ContractsService {
  private fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>;

  constructor(fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>) {
    this.fetch = fetch;
  }

  /**
   * Get policy coverage (obter-cobertura-apolice).
   * @param identContrato - Contract id (required)
   * @param identSubestipulante - Sub-stipulator id (required)
   * @param certificadoAp - Policy certificate (optional)
   * @param identEndosso - Endorsement id (optional)
   * @param page - default 0
   * @param size - page size
   */
  async getCoverage(
    identContrato: number,
    identSubestipulante: number,
    certificadoAp?: number,
    identEndosso?: number,
    page: number = 0,
    size?: number
  ): Promise<ApiCoverageResponse> {
    const url =
      VIDA_CONTRACTS_ROUTES.GET_CONTRACTS_COVERAGE +
      buildQueryString({
        identContrato,
        identSubestipulante,
        certificadoAp,
        identEndosso,
        page,
        size,
      });
    return this.fetch<ApiCoverageResponse>(url, { method: 'GET' });
  }

  /**
   * Get stipulator data (obter-dados-estipulante).
   * @param identContratoEst - Stipulator contract id (required)
   * @param codEmpresaEst - Company code (optional)
   * @param codSucursalEst - Branch code (optional)
   * @param codRamoEst - Line of business code (optional)
   * @param codprodutoEst - Product code (optional)
   * @param numApoliceEst - Policy number (optional)
   */
  async getEstipulanteData(
    identContratoEst: number,
    codEmpresaEst?: number,
    codSucursalEst?: number,
    codRamoEst?: number,
    codprodutoEst?: number,
    numApoliceEst?: number
  ): Promise<ApiStipulatorResponse> {
    const url =
      VIDA_CONTRACTS_ROUTES.GET_CONTRACTS_ESTIPULANT_DATA +
      buildQueryString({
        identContratoEst,
        codEmpresaEst,
        codSucursalEst,
        codRamoEst,
        codprodutoEst,
        numApoliceEst,
      });
    return this.fetch<ApiStipulatorResponse>(url, { method: 'GET' });
  }

  /**
   * Get sub-stipulator uniform capital (obter-dados-subestipulante-capital-uniforme).
   * @param identificadorSubestipulante - Sub-stipulator id (required)
   * @param identAbertoPorCoberetura - Open by coverage flag (required, e.g. "S")
   * @param page - default 0
   * @param size - optional
   */
  async getCapitalUniforme(
    identificadorSubestipulante: number,
    identAbertoPorCoberetura: string,
    page: number = 0,
    size?: number
  ): Promise<ApiUniformCapitalResponse> {
    const url =
      VIDA_CONTRACTS_ROUTES.GET_CONTRACTS_ESTIPULANT_CAPITAL_UNIFORM +
      buildQueryString({
        identificadorSubestipulante,
        identAbertoPorCoberetura,
        page,
        size,
      });
    return this.fetch<ApiUniformCapitalResponse>(url, { method: 'GET' });
  }

  /**
   * Get minimum age limit (obter-dados-subestipulante-limite-de-idade-minima).
   * @param identificadorSubestipulante - Sub-stipulator id (required)
   */
  async getAgeLimitMin(identificadorSubestipulante: number): Promise<ApiMinimumAgeResponse> {
    const url =
      VIDA_CONTRACTS_ROUTES.GET_CONTRACTS_AGE_LIMIT +
      buildQueryString({ identificadorSubestipulante });
    return this.fetch<ApiMinimumAgeResponse>(url, { method: 'GET' });
  }

  /**
   * Get max age for first invoice (obter-dados-subestipulante-limite-de-idade-maxima-para-primeira-fatura).
   * @param identificadorSubestipulante - Sub-stipulator id (required)
   */
  async getAgeLimitFirstInvoice(
    identificadorSubestipulante: number
  ): Promise<ApiMaxAgeFirstInvoiceResponse> {
    const url =
      VIDA_CONTRACTS_ROUTES.GET_CONTRACTS_AGE_LIMIT_FIRST_CONTRACT +
      buildQueryString({ identificadorSubestipulante });
    return this.fetch<ApiMaxAgeFirstInvoiceResponse>(url, { method: 'GET' });
  }

  /**
   * Get max age for other invoices (obter-dados-subestipulante-limite-de-idade-maxima-demais-faturas).
   * @param identificadorSubestipulante - Sub-stipulator id (required)
   */
  async getAgeLimitOtherInvoices(
    identificadorSubestipulante: number
  ): Promise<ApiMaxAgeOtherInvoicesResponse> {
    const url =
      VIDA_CONTRACTS_ROUTES.GET_CONTRACTS_AGE_ALL_LIMIT_MAX_OTHER +
      buildQueryString({ identificadorSubestipulante });
    return this.fetch<ApiMaxAgeOtherInvoicesResponse>(url, { method: 'GET' });
  }
}
